/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

// Component imports
import { Checkbox, Collapse, Form, Input, InputNumber, Select } from 'antd';
import { ArrowDownIcon } from '../../../Icons/ArrowDownIcon';

// Misc imports
import { useBotLanguage } from '../../../../utils/hooks/useBotLanguage';
import LabelWithOriginalText from '../../../Forms/Fields/LabelWithOriginalText';
import { TEXT_INPUT_TYPES } from '../../../../constants/flowGraph';

// Constant declarations
const { Option } = Select;

const TextInputNodeForm = ({ form }) => {
  const { t } = useTranslation();
  const { isEditingPrimaryLanguage, botLanguages, currentBotLanguage, primaryBotLanguage } = useBotLanguage();
  const isDateInputType = form.getFieldValue(['data', 'inputType']) === TEXT_INPUT_TYPES.DATE;
  return (
    <Fragment>
      <Form.Item
        label={t('Input type')}
        name={['data', 'inputType']}
        rules={[{ required: true, message: t('Please select an input type.') }]}
      >
        <Select
          placeholder={t('Select an input type')}
          size="large"
          getPopupContainer={triggerNode => triggerNode.parentElement}
          disabled={!isEditingPrimaryLanguage}
        >
          {
            Object.entries(TEXT_INPUT_TYPES).map(([key, value]) => (
              <Option key={key} value={value}>{t(value, { context: 'text input type' })}</Option>
            ))
          }
        </Select>
      </Form.Item>

      {
        !isDateInputType ? (
          <LabelWithOriginalText label={t('Input placeholder')} originalText={form.getFieldValue(['data', 'label', primaryBotLanguage])} />

        ) : null
      }

      {
        botLanguages.map(lang => (
          <Form.Item
            label=""
            name={['data', 'label', lang]}
            initialValue={isDateInputType ? '' : form.getFieldValue(['data', 'label', currentBotLanguage])}
            hidden={currentBotLanguage !== lang || isDateInputType}
            rules={[{ required: lang === primaryBotLanguage && isEditingPrimaryLanguage && !isDateInputType, message: t('Placeholder cannot be blank.') }]}
          >
            <Input
              size="large"
              placeholder={t('Enter a placeholder for the user input')}
            />
          </Form.Item>
        ))
      }

      <Collapse
        bordered={false}
        expandIcon={({ isActive }) => <ArrowDownIcon rotate={isActive ? 180 : 0} />}
      >
        <Collapse.Panel
          header={t('Advanced Settings')}
          forceRender
        >
          <LabelWithOriginalText label={t('Invalid input message')} originalText={form.getFieldValue(['data', 'validationFallbackMessage', primaryBotLanguage])} />
          {
            botLanguages.map(lang => (
              <Form.Item
                label=""
                name={['data', 'validationFallbackMessage', lang]}
                hidden={currentBotLanguage !== lang}
              >
                <Input
                  size="large"
                  placeholder={t('Enter a message for invalid input')}
                />
              </Form.Item>
            ))
          }

          <Form.Item
            label={t('Maximum allowed characters')}
            name={['data', 'inputMaxCharacters']}
            className="mt-3"
          >
            <InputNumber
              size="large"
              placeholder={t('Enter a number')}
              min={1}
              max={10000}
              className="w-100"
              disabled={!isEditingPrimaryLanguage}
            />
          </Form.Item>

          <Form.Item
            label=""
            name={['data', 'inputIsMandatory']}
            valuePropName="checked"
            className="mb-0"
            disabled={!isEditingPrimaryLanguage}
          >
            <Checkbox>
              {t('Mandatory')}
            </Checkbox>
          </Form.Item>

          <Form.Item
            label=""
            name={['data', 'inputIsMultiLine']}
            valuePropName="checked"
            className="mb-0"
            disabled={!isEditingPrimaryLanguage}
          >
            <Checkbox>
              {t('Display as multi-line')}
            </Checkbox>
          </Form.Item>
        </Collapse.Panel>
      </Collapse>
    </Fragment>
  );
};

TextInputNodeForm.propTypes = {
  form: PropTypes.object.isRequired
};

export default TextInputNodeForm;
